import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Form, Input, DatePicker, Tooltip, Switch } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import StateSelect from 'components/form/StateSelect';
import UsPhoneInput from 'components/form/USPhoneInput';
import s from './s.module.less';
import { RepresentativeAndPersonalInfo } from 'types/paymentSetup';
import dayjs from 'dayjs';
import FormValidationHelper from 'utils/validation';
import AutoAddressInput from 'components/AutoAddressInput';
import useSettings from 'hooks/useSettings';
import { sortByProperty } from 'utils/form';
import { EnumFields } from 'types/enumerationData';

interface PersonalFormProps {
    value?: Partial<RepresentativeAndPersonalInfo>;
    onChange?: (changedValues: Partial<RepresentativeAndPersonalInfo>) => void;
    onSameAsRepChange?: (sameAsRep: boolean) => void;
    isOwner?: boolean;
    sameAsRep?: boolean;
    title?: string;
}

const PersonalForm: React.FC<PersonalFormProps> = forwardRef(({
    value = {},
    onChange,
    onSameAsRepChange,
    isOwner = false,
    sameAsRep = false,
    title = '',
}, ref) => {
    const [form] = Form.useForm<RepresentativeAndPersonalInfo>();
    const [isSameAsRep, setIsSameAsRep] = useState(false);
    const [setting] = useSettings();
    const stateOption = sortByProperty(setting[EnumFields.STATE] || [], 'label');

    const taxIdLabel = isOwner ? "Owner's Tax ID" : 'Representative Tax ID';
    const taxIdExtra = isOwner
        ? 'Enter the last 4 digits of the owner\'s U.S. Social Security Number (SSN).'
        : 'Enter the last 4 digits of the representative\'s U.S. Social Security Number (SSN).';

    useEffect(() => {
        if (value) {
            form.setFieldsValue({
                ...value,
                birthday: value.birthday ? dayjs(value.birthday) : undefined,
            });
        }
        setIsSameAsRep(sameAsRep);
    }, [value, form, sameAsRep]);

    const handleSameAsRepChange = (checked: boolean) => {
        setIsSameAsRep(checked);
        onSameAsRepChange?.(checked);
    };

    const handleValuesChange = (
        changedValues: Partial<RepresentativeAndPersonalInfo>,
        allValues: Partial<RepresentativeAndPersonalInfo>,
    ) => {
        let birthdayVal;
        if (allValues.birthday) {
            birthdayVal = typeof allValues.birthday === 'string' ? allValues?.birthday : allValues.birthday?.format('YYYY-MM-DD');
        }
        if (onChange) {
            onChange({ ...allValues, birthday: birthdayVal });
        }
    };

    useImperativeHandle(ref, () => ({
        validateFields: () => {
            return form.validateFields();
        },
    }));

    if (!setting || !setting[EnumFields.STATE]) {
        return null;
    }

    return (
        <div className={s.personalFormContainer}>
            <div className={s.titleContainer}>
                <h3 className={s.sectionTitle}>{title}</h3>
                {isOwner && (
                    <div className={s.toggleContainer}>
                        <span className={s.toggleLabel}>Same as representative</span>
                        <Tooltip title="Toggle on if the owner is the same person as the account representative. The information will be auto-filled for you.">
                            <QuestionCircleOutlined className={s.tooltipIcon} />
                        </Tooltip>
                        <Switch checked={isSameAsRep} onChange={handleSameAsRepChange} className={s.switch} />
                    </div>
                )}
            </div>

            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
            >
                <Form.Item name="type" hidden>
                    <Input />
                </Form.Item>

                <div className={s.formRow}>
                    <Form.Item
                        label="First name"
                        name="firstName"
                        rules={[{ required: true, message: 'Please enter first name' }]}
                        className={s.formCol}
                    >
                        <Input placeholder="First name" disabled={isOwner && sameAsRep} />
                    </Form.Item>
                    <Form.Item
                        label="Middle name"
                        name="middleName"
                        className={s.formCol}
                    >
                        <Input placeholder="Middle name" disabled={isOwner && sameAsRep} />
                    </Form.Item>
                    <Form.Item
                        label="Last name"
                        name="lastName"
                        rules={[{ required: true, message: 'Please enter last name' }]}
                        className={s.formCol}
                    >
                        <Input placeholder="Last name" disabled={isOwner && sameAsRep} />
                    </Form.Item>
                </div>

                <Form.Item
                    label="Date of birth"
                    name="birthday"
                    rules={[{ required: true, message: 'Please select date of birth' }]}
                >
                    <DatePicker
                        style={{ width: '399px' }}
                        placeholder="yyyy-mm-dd"
                        disabled={isOwner && sameAsRep}
                        defaultPickerValue={dayjs().subtract(18, 'year')}
                    />
                </Form.Item>

                <div className={s.formRow}>
                    <Form.Item
                        label="Phone number"
                        name="phoneNumber"
                        rules={[
                            { required: true, message: 'Please enter phone number' }, FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit phone number'),
                        ]}
                        className={s.formCol}
                    >
                        <UsPhoneInput disabled={isOwner && sameAsRep} />
                    </Form.Item>
                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Invalid email format' }]}
                        className={s.formCol}
                    >
                        <Input type="email" placeholder="Email address" disabled={isOwner && sameAsRep} />
                    </Form.Item>
                </div>

                <Form.Item
                    label="Address"
                    name="address"
                    rules={[{ required: true, message: 'Please enter address' }]}
                >
                    <AutoAddressInput
                        placeholder="Street address"
                        disabled={isOwner && sameAsRep}
                        addCityToAddress
                        onStateChange={(val) => {
                            const targetState = stateOption.find((item) => item.value === val || item.label === val);
                            if (targetState) {
                                form.setFieldsValue({
                                    state: targetState.value,
                                });
                            }
                        }}
                        onCityChange={(val) => {
                            form.setFieldsValue({
                                city: val,
                            });
                        }}
                        onPostcodeChange={(val) => {
                            form.setFieldsValue({
                                zip: val,
                            });
                        }}
                    />
                </Form.Item>

                <div className={s.formRow}>
                    <Form.Item
                        label="City"
                        name="city"
                        rules={[{ required: true, message: 'Please enter city' }]}
                        className={s.formCol}
                    >
                        <Input placeholder="City" disabled={isOwner && sameAsRep} />
                    </Form.Item>
                    <Form.Item
                        label="State"
                        name="state"
                        rules={[{ required: true, message: 'Please select state' }]}
                        className={s.formCol}
                    >
                        <StateSelect disabled={isOwner && sameAsRep} />
                    </Form.Item>
                    <Form.Item
                        label="ZIP code"
                        name="zip"
                        rules={[{ required: true, message: 'Please enter ZIP code' }]}
                        className={s.formCol}
                    >
                        <Input placeholder="ZIP code" disabled={isOwner && sameAsRep} />
                    </Form.Item>
                </div>

                <Form.Item
                    label={taxIdLabel}
                    name="taxId"
                    rules={[{ required: true, message: 'Please enter Tax ID' }]}
                    extra={taxIdExtra}
                >
                    <Input placeholder="Last 4 digits of SSN" maxLength={4} disabled={isOwner && sameAsRep} />
                </Form.Item>
            </Form>
        </div>
    );
});

export default PersonalForm;
